/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 09 2024
 **/

import { useAtomCallback } from "jotai/utils";

import { JotaiStore } from "@/frameworks/libraries/commons";

import { CreateSpaceState } from "./useCreateSpaceState";
import { initSpaceStateFn } from "./private/utils";
import { CreateSpaceGroupIDAtom } from "@/frameworks/core/components";

export default function useCreateSpaceActions() {
	const update = useAtomCallback(
		(_, set, payload: Partial<CreateSpaceStateType>) => {
			set(CreateSpaceState, (prev: CreateSpaceStateType) => ({
				...prev,
				...payload
			}));
		},
		{
			store: JotaiStore
		}
	);

	const resetGroupID = useAtomCallback(
		(_, set) => {
			set(CreateSpaceGroupIDAtom, null);
		},
		{ store: JotaiStore }
	);

	const reset = useAtomCallback(
		(get, set) => {
			const state = get(CreateSpaceState);
			set(CreateSpaceState, initSpaceStateFn({ accessAllowType: state.accessAllowType }));
			resetGroupID();
		},
		{ store: JotaiStore }
	);

	const init = useAtomCallback(
		(get, set) => {
			const state = get(CreateSpaceState);
			set(CreateSpaceState, initSpaceStateFn({ accessAllowType: state.accessAllowType }));
		},
		{ store: JotaiStore }
	);

	const getState = useAtomCallback(
		(get) => {
			return get(CreateSpaceState);
		},
		{ store: JotaiStore }
	);

	const getInitState = useAtomCallback(
		(get) => {
			return initSpaceStateFn({ accessAllowType: get(CreateSpaceState).accessAllowType });
		},
		{ store: JotaiStore }
	);

	return {
		init,
		reset,
		update,
		getState,
		getInitState
	};
}
